import * as rd from '@devexperts/remote-data-ts'
import { PayloadAction } from '@reduxjs/toolkit'
import { BookingPeriod } from '@spiaggeit/spit-datepicker'
import { fetchCartQuotation } from '@/api/quote'
import { Service, TimeSchedule } from '@/models/service'
import { convertLocalTimeToUTCTime } from '@/utils/dateUtils'
import { getHalfDayValueFromBookingPeriod } from '@/utils/halfDay'
import { getTimeScheduleKey } from '@/utils/timeSchedules'
import { appSlice } from '../appSlice'
import { RootState } from '../configureStore'
import { createAppSlice } from '../createAppSlice'
import {
  insertPeriodSlice,
  periodAsUnixTimeSelector,
} from '../insertPeriodSlice'
import { licenseSlice } from '../licenseSlice'

export type AddedServices = Record<
  string,
  {
    bought: number
    timeSchedule: TimeSchedule
  }
>

interface InitialState {
  addedServices: AddedServices
  price: rd.RemoteData<string, number>
  selectedDays: number[]
  service: Service | null
}

const initialState: InitialState = {
  addedServices: {},
  price: rd.success(0),
  selectedDays: [],
  service: null,
}

export const timeScheduleSlice = createAppSlice({
  initialState,
  name: 'timeSchedule',
  reducers: (create) => ({
    changeTimeScheduleAmount: create.reducer(
      (
        state,
        action: PayloadAction<{ timeSchedule: TimeSchedule; amount: number }>
      ) => {
        const key = getTimeScheduleKey(action.payload.timeSchedule)

        if (action.payload.amount < 1) {
          // remove
          delete state.addedServices[key]
        } else {
          // add
          state.addedServices[key] = {
            bought: action.payload.amount,
            timeSchedule: action.payload.timeSchedule,
          }
        }
      }
    ),
    daySelected: create.reducer((state, action: PayloadAction<number>) => {
      if (state.selectedDays.includes(action.payload)) {
        state.selectedDays = state.selectedDays.filter(
          (d) => d !== action.payload
        )
      } else {
        state.selectedDays.push(action.payload)
      }
    }),
    quote: create.asyncThunk(
      async (_, thunkApi): Promise<number> => {
        const rootState = thunkApi.getState() as RootState

        const addedServices =
          timeScheduleSlice.selectors.addedServices(rootState)
        const bookingPeriod =
          insertPeriodSlice.selectors.bookingPeriod(rootState)
        const channel = appSlice.selectors.channel(rootState)
        const country = appSlice.selectors.country(rootState)
        const licenseCode = licenseSlice.selectors.licenseCode(rootState)
        const period = periodAsUnixTimeSelector(rootState)
        const service = timeScheduleSlice.selectors.service(rootState)
        const days = timeScheduleSlice.selectors.selectedDays(rootState)

        if (service === null || period === null) {
          return 0
        }

        const boughtServices = Object.values(addedServices).reduce<
          {
            bought: number
            day: number
            endTime: string
            serviceId: number
            startTime: string
          }[]
        >((acc, addedService) => {
          days.forEach((time) => {
            const day = convertLocalTimeToUTCTime(time)
            acc.push({
              bought: addedService.bought,
              day,
              endTime: addedService.timeSchedule.endTime,
              serviceId: service.serviceId,
              startTime: addedService.timeSchedule.startTime,
            })
          })
          return acc
        }, [])

        const res = await fetchCartQuotation({
          country,
          licenseName: licenseCode,
          payload: {
            channel,
            reservations: {
              0: {
                beds: 0,
                boughtServices,
                chairs: 0,
                deckChairs: 0,
                endDate: period.end,
                halfDay: getHalfDayValueFromBookingPeriod(bookingPeriod),
                maxiBeds: 0,
                notes: '',
                seasonal: bookingPeriod === BookingPeriod.ALL_SEASON,
                sectorId: 0,
                sectorName: '',
                services: {},
                spotName: null,
                spotType: null,
                startDate: period.start,
              },
            },
          },
        })

        if (
          res.status === 'error' ||
          !Array.isArray(res.data.result.reservationPrices) ||
          res.data.result.reservationPrices.length === 0
        ) {
          return 0
        }

        return res.data.result.reservationPrices[0] ?? 0
      },
      {
        fulfilled: (state, action) => {
          state.price = rd.success(action.payload)
        },
        pending: (state) => {
          state.price = rd.pending
        },
      }
    ),
    reset: create.reducer(() => initialState),
    setAddedServices: create.reducer(
      (state, action: PayloadAction<AddedServices>) => {
        state.addedServices = action.payload
      }
    ),
    setSelectedDays: create.reducer(
      (state, action: PayloadAction<number[]>) => {
        state.selectedDays = action.payload
      }
    ),
    setService: create.reducer(
      (state, action: PayloadAction<Service | null>) => {
        state.service = action.payload
      }
    ),
  }),
  selectors: {
    addedServices: (state) => state.addedServices,
    price: (state) => state.price,
    selectedDays: (state) => state.selectedDays,
    service: (state) => state.service,
  },
})
